import { useCallback, useRef } from 'react'

/**
 * Guards an async action against re-entry from a second click that lands before the first
 * click's disabled-state re-render has committed. React state (e.g. a `pending` flag) updates
 * on the next render, so two clicks fired in the same tick both see the old, enabled state and
 * both fire the mutation. A ref updates synchronously, so the second call sees the lock the
 * first call just took and is dropped.
 *
 * Use one lock per action — each guards its own mutation, so unrelated actions (e.g. adding one
 * plan while extending another) are not blocked by each other.
 */
export const useActionLock = () => {
    const lockedRef = useRef(false)

    return useCallback(async (action) => {
        if (lockedRef.current) return undefined
        lockedRef.current = true
        try {
            return await action()
        } finally {
            lockedRef.current = false
        }
    }, [])
}

export default useActionLock
